import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IAppSettings, PaywallMode } from '@/MODELS/appSettings.model'
import { ISubscription } from '@/MODELS/subscription.model'

import { api } from '@/services/api.service'
import { useAuth } from '@/services/authentication.service'
import { useResource } from '@/services/resource.service'

type SubscriptionStatus =
    | 'active'
    | 'trialing'
    | 'paused'
    | 'canceled'
    | 'incomplete'
    | 'incomplete_expired'
    | 'past_due'
    | 'unpaid'
    | null

export interface IUsageStatus {
    completedMilestones: number
    chatConversations: number
    thresholdMilestones: number
    thresholdChats: number
    thresholdReached: boolean
    paywallDismissCount: number
}

interface IPaywallContextValue {
    subscription: ISubscription | null
    hasAccess: boolean
    isLoading: boolean
    error: string | null
    isTestMode: boolean
    trialPeriodDays: number
    paywallMode: PaywallMode
    usageStatus: IUsageStatus | null
    startSubscription: (returnUrl?: string) => Promise<void>
    manageSubscription: () => Promise<void>
    refreshSubscription: () => Promise<void>
    refreshUsageStatus: () => Promise<IUsageStatus | null>
    getSubscriptionStatus: () => SubscriptionStatus
    checkoutReturnState: {
        isReturningFromCheckout: boolean
        success: boolean
        clearCheckoutReturn: () => void
    }
}

const PaywallContext = createContext<IPaywallContextValue | undefined>(undefined)

interface IPaywallProvider {
    children: ReactNode
    requiredSubscriptionStatus?: SubscriptionStatus[]
}
export function PaywallProvider({ children, requiredSubscriptionStatus = ['active', 'trialing'] }: IPaywallProvider) {
    const { user } = useAuth()
    const [subscription, setSubscription, subscriptionResource] = useResource<ISubscription>('stripe/subscription')
    const [appSettings] = useResource<IAppSettings>(ROUTES.app.appSettings)

    const [usageStatus, setUsageStatus] = useState<IUsageStatus | null>(null)
    const [error, setError] = useState<string | null>(null)

    const [isReturningFromCheckout, setIsReturningFromCheckout] = useState<boolean>(false)
    const [checkoutReturnSuccess, setCheckoutReturnSuccess] = useState<boolean>(false)

    const paywallMode: PaywallMode = appSettings?.paywallMode || 'trial'
    const location = useLocation()
    const lastFetchPath = useRef<string>('')

    // Fetch usage status when in usage mode — on mount and on every navigation
    // Skip for test users with paywall off — they always have access
    useEffect(() => {
        if (user?.testUser?.enabled && user.testUser.paywallMode === 'off') return

        if (appSettings?.paywallEnabled && paywallMode === 'usage') {
            // Avoid duplicate fetches for the same path (e.g. re-renders)
            if (lastFetchPath.current !== location.pathname) {
                lastFetchPath.current = location.pathname
                fetchUsageStatus()
            }
        }
    }, [appSettings?.paywallEnabled, paywallMode, location.pathname, user?.testUser?.enabled, user?.testUser?.paywallMode])

    const fetchUsageStatus = async (): Promise<IUsageStatus | null> => {
        try {
            const response = await api<IUsageStatus>(ROUTES.app.usageStatus)
            if (response?.data) {
                setUsageStatus(response.data)
                return response.data
            }
            return null
        } catch (err) {
            console.error('Failed to fetch usage status:', err)
            return null
        }
    }

    const refreshUsageStatus = async (): Promise<IUsageStatus | null> => {
        return await fetchUsageStatus()
    }

    const hasAccess = (): boolean => {
        // Test user with paywall off = always has access
        if (user?.testUser?.enabled && user.testUser.paywallMode === 'off') {
            return true
        }

        // If paywall setting hasn't loaded yet or is disabled, grant access
        if (!appSettings?.paywallEnabled) {
            return true
        }

        // Usage-based mode: grant access until threshold is reached
        if (paywallMode === 'usage') {
            // If we have an active subscription, always grant access
            if (subscription && requiredSubscriptionStatus.includes(subscription.subscriptionStatus as SubscriptionStatus)) {
                return true
            }
            // Check benefits end date for canceled subscriptions
            if (subscription?.benefitsEndDate && new Date(subscription.benefitsEndDate) > new Date()) {
                return true
            }
            // If usage status hasn't loaded yet, grant access (don't block while loading)
            if (!usageStatus) return true
            // Grant access if under threshold
            return !usageStatus.thresholdReached
        }

        // Trial-based mode (existing behavior)
        if (!subscription) return false

        // Active or trialing subscriptions always have access
        if (requiredSubscriptionStatus.includes(subscription.subscriptionStatus as SubscriptionStatus)) {
            return true
        }

        // For canceled/paused/other statuses: grant access if benefitsEndDate is still in the future
        if (subscription.benefitsEndDate) {
            return new Date(subscription.benefitsEndDate) > new Date()
        }

        return false
    }

    const getSubscriptionStatus = (): SubscriptionStatus => {
        return (subscription?.subscriptionStatus as SubscriptionStatus) || null
    }

    const startSubscription = async (returnUrl?: string): Promise<void> => {
        try {
            const response = await api<{ url: string; message?: string }>('stripe/createSubscriptionCheckoutSession', {
                returnUrl,
            })

            if (response?.error) {
                setError(response.error)
                return
            }

            if (response?.data?.url) {
                window.location.assign(response.data.url)
            }
        } catch (err) {
            setError('Failed to start subscription process')
            console.error('Subscription Error ', err)
        }
    }

    const manageSubscription = async (): Promise<void> => {
        try {
            const response = await api<{ url: string }>(ROUTES.stripe.createSubscriptionPortalSession)
            if (response?.error) {
                setError(response.error)
                return
            }

            if (response?.data?.url) {
                window.location.assign(response.data.url)
            }
        } catch (err) {
            setError('Failed to open subscription management portal')
            console.error('Subscription management error:', err)
        }
    }

    const refreshSubscription = async (): Promise<void> => {
        try {
            await subscriptionResource.get()
            setError(null)
        } catch (err) {
            setError('Failed to refresh subscription data')
            console.error('Refresh error:', err)
        }
    }

    const clearCheckoutReturn = () => {
        setIsReturningFromCheckout(false)

        const url = new URL(window.location.href)
        if (url.searchParams.has('success')) {
            url.searchParams.delete('success')
            window.history.replaceState({}, document.title, url.toString())
        }
    }

    useEffect(() => {
        const query = new URLSearchParams(window.location.search)
        const successParam = query.get('success')

        if (successParam === 'true' || successParam === 'false') {
            setIsReturningFromCheckout(true)
            setCheckoutReturnSuccess(successParam === 'true')

            refreshSubscription()
        }
    }, [window.location.search])

    const value: IPaywallContextValue = {
        subscription,
        isLoading: subscriptionResource.isLoading || false,
        error,
        hasAccess: hasAccess(),
        isTestMode: user?.testUser?.enabled
            ? user.testUser.paywallMode === 'stripe_test'
            : appSettings?.stripeMode === 'test',
        trialPeriodDays: appSettings?.trialPeriodDays ?? 30,
        paywallMode,
        usageStatus,
        startSubscription,
        manageSubscription,
        refreshSubscription,
        refreshUsageStatus,
        getSubscriptionStatus,
        checkoutReturnState: {
            isReturningFromCheckout,
            success: checkoutReturnSuccess,
            clearCheckoutReturn,
        },
    }

    return <PaywallContext.Provider value={value}>{children}</PaywallContext.Provider>
}

export const usePaywall = (): IPaywallContextValue => {
    const context = useContext(PaywallContext)
    if (context === undefined) {
        throw new Error('usePaywall must be used within a PaywallProvider')
    }
    return context
}
